import { createBindingKey } from "../../market-data/bindings";
import { getProviderCapability, type ProviderCapability } from "../../market-data/providerScope";
import type { MarketBinding, MarketSnapshot, ResolvedMarket } from "../../market-data/types";
import type { MarketDataSessionState } from "../services/marketDataSession";
import { isProbability, structuredCloneSafe, type ExternalMarketConnection, type MarketContract, type MarketDataPublicationSnapshot, type MarketSourceMode } from "./types";

export function marketSourceMode(market: MarketContract): MarketSourceMode {
  return market.sourceMode ?? "ftm_internal";
}

export function externalConnection(market: MarketContract): ExternalMarketConnection | null {
  return marketSourceMode(market) === "ftm_internal" ? null : market.externalConnection ?? null;
}

export function isExternalMarket(market: MarketContract): boolean {
  return marketSourceMode(market) !== "ftm_internal";
}

export function marketProviderCapability(market: MarketContract): ProviderCapability | null {
  const sourceMode = marketSourceMode(market);
  return sourceMode === "ftm_internal" ? null : getProviderCapability(sourceMode);
}

export function liveMarketDataEnabled(market: MarketContract): boolean {
  return marketProviderCapability(market)?.productionMarketDataAllowed ?? false;
}

export function marketBinding(market: MarketContract): MarketBinding | null {
  return externalConnection(market)?.binding ?? null;
}

export function currentMarketProbability(market: MarketContract, runtime?: Pick<MarketDataSessionState, "latest" | "lastKnown"> | null): number | null {
  if (isExternalMarket(market)) return runtimeSnapshot(runtime)?.probability ?? null;
  return isProbability(market.currentMarketProbability) ? market.currentMarketProbability : null;
}

export function marketRuntimeSnapshot(runtime?: Pick<MarketDataSessionState, "latest" | "lastKnown"> | null): MarketSnapshot | null {
  return runtimeSnapshot(runtime);
}

export function createMarketDataPublicationSnapshot(market: MarketContract, runtime: MarketDataSessionState | null | undefined): MarketDataPublicationSnapshot | null {
  const connection = externalConnection(market);
  const snapshot = runtimeSnapshot(runtime);
  if (!connection || !snapshot || !isProbability(snapshot.probability)) return null;
  return {
    sourceMode: connection.sourceMode,
    binding: structuredCloneSafe(connection.binding),
    sourceUrl: connection.sourceUrl,
    probability: snapshot.probability,
    observedAt: snapshot.observedAt,
    retrievedAt: snapshot.receivedAt,
    pricingMethod: snapshot.pricingMethod,
    freshness: snapshot.freshness,
    providerStatus: connection.importedStatus,
  };
}

export function matchesMarketDataPublication(market: MarketContract, publication: MarketDataPublicationSnapshot | null | undefined): boolean {
  const connection = externalConnection(market);
  return Boolean(connection && publication && publication.sourceMode === connection.sourceMode && createBindingKey(publication.binding) === createBindingKey(connection.binding));
}

export function marketAtPublication(market: MarketContract, publication: MarketDataPublicationSnapshot | null | undefined): MarketContract {
  if (!publication || !isExternalMarket(market) || !matchesMarketDataPublication(market, publication)) return structuredCloneSafe(market);
  return structuredCloneSafe({
    ...market,
    currentMarketProbability: publication.probability,
    marketPriceUpdatedAt: publication.observedAt ?? publication.retrievedAt,
    platform: publication.sourceMode === "polymarket" ? "Polymarket" : "Kalshi",
    marketUrl: publication.sourceUrl,
    marketStatus: externalConnection(market)?.importedStatus ?? market.marketStatus,
  });
}

export function connectExternalMarket(
  market: MarketContract,
  resolved: ResolvedMarket,
  connectedAt: string,
): MarketContract {
  const connection: ExternalMarketConnection = {
    sourceMode: resolved.provider,
    binding: structuredCloneSafe(resolved.binding),
    sourceUrl: resolved.sourceUrl,
    importedQuestion: resolved.question,
    importedCloseTime: resolved.closeTime,
    importedResolutionCriteria: resolved.resolutionCriteria,
    importedStatus: resolved.status,
    resolutionReview: "pending",
    connectedAt,
  };
  const title = market.title.trim() ? market.title : resolved.question;
  return structuredCloneSafe({
    ...market,
    title,
    slug: market.slug || bindingSlug(resolved.binding),
    platform: displayProvider(resolved.provider),
    marketUrl: resolved.sourceUrl,
    currentMarketProbability: null,
    marketPriceUpdatedAt: null,
    resolutionCriteria: resolved.resolutionCriteria,
    resolutionDate: resolved.closeTime ? resolved.closeTime.slice(0, 10) : null,
    marketStatus: resolved.status,
    sourceMode: resolved.provider,
    externalConnection: connection,
  });
}

export function disconnectExternalMarket(market: MarketContract): MarketContract {
  return structuredCloneSafe({
    ...market,
    sourceMode: "ftm_internal",
    externalConnection: null,
    platform: "",
    marketUrl: "",
    currentMarketProbability: null,
    marketPriceUpdatedAt: null,
    resolutionCriteria: "",
    resolutionDate: null,
    marketStatus: "draft",
  });
}

function bindingSlug(binding: MarketBinding): string {
  return binding.provider === "polymarket" ? binding.marketSlug ?? binding.marketId : binding.ticker;
}

function displayProvider(provider: "polymarket" | "kalshi"): string {
  return provider === "polymarket" ? "Polymarket" : "Kalshi";
}

function runtimeSnapshot(runtime?: Pick<MarketDataSessionState, "latest" | "lastKnown"> | null): MarketSnapshot | null {
  return runtime?.latest ?? runtime?.lastKnown ?? null;
}
